import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import Button from '@renderer/components/Button'
import { COUNTDOWN_PLACEHOLDER, isCountdownUrgent, useCountdown } from '@renderer/utils/countdownUtils'
import { useTranslation } from 'react-i18next'
import { FrameOption, FrameCategory } from '@renderer/types/configState'
import { BackgroundContainer, GlassPanel } from '@renderer/components/BackgroundContainer'
import { useBackgroundTextColor } from '@renderer/hooks/useBackgroundTextColor'
import { useDesignLayout, useSessionLayout } from '@renderer/hooks'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'

const FramePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const {
    frames: allFrames,
    frameCategories,
    config: globalConfig,
    getPageSettings
  } = useConfigStore()
  const config = globalConfig?.pages.frame
  // Frame titles are shown by default; only hidden when explicitly disabled in Studio.
  const showFrameTitle = config?.settings?.displayFrameTitle !== false
  const { primaryColor, secondaryColor } = globalConfig?.theme || {
    primaryColor: '#3B82F6',
    secondaryColor: '#FFFFFF'
  }
  const { setSessionState, logActivity } = useSessionStateStore()
  const [selectedCategory, setSelectedCategory] = useState('all')
  const {
    isPortrait: isPotrait,
    isCompact: compactFrameLayout,
    designW,
    designH,
    shortSide
  } = useDesignLayout()
  const { isCompact: viewportCompact, isLandscapeViewport } = useSessionLayout()
  const viewportLongSide = Math.max(designW, designH)
  /** Physical phone/tablet in landscape — many columns fit; design canvas may still be “portrait” */
  const frameMobileLandscape = viewportCompact && isLandscapeViewport
  /** Compact + design portrait: preview on top, grid below */
  const stackPreviewTop = compactFrameLayout && isPotrait
  const frameLandscapeCompact = compactFrameLayout && !isPotrait
  /**
   * Large landscape row layout: 4 columns only for very wide canvases (>2400px).
   * Standard 1920 designs stay at 3 cols for a premium, uncrowded feel (matching legacy).
   */
  const frameGridLargeLandscape =
    !stackPreviewTop &&
    isLandscapeViewport &&
    viewportLongSide > 2400 &&
    !compactFrameLayout
  /** Row preview pane: same default Button sizing as Select / Filter (no full-width bar). */
  const frameNextRowBtn = compactFrameLayout ? '!px-4 !py-2 !text-sm' : undefined
  /**
   * Stacked mobile portrait: floating Next — match Select.tsx selectNextStackedBtn exactly.
   */
  const frameNextStackedBtn =
    compactFrameLayout && frameMobileLandscape
      ? '!inline-flex !h-9 !min-h-9 !w-auto !max-w-[9.5rem] !shrink-0 !items-center !justify-center !px-3 !py-1.5 !text-xs !leading-snug'
      : compactFrameLayout
        ? '!inline-flex !h-10 !min-h-10 !w-auto !max-w-[11rem] !shrink-0 !items-center !justify-center !px-4 !py-2 !text-sm !leading-snug'
        : '!inline-flex !min-h-[3.25rem] !w-auto !max-w-[14rem] !items-center !justify-center !px-6 !py-3 !text-lg !leading-snug'
  /** Thumbnail fits canvas aspect inside max area without overflowing */
  const framePreviewAspectBox =
    stackPreviewTop ||
    frameLandscapeCompact ||
    (!stackPreviewTop && isLandscapeViewport)
  /**
   * Full-size landscape row (e.g. 1920×1080): default preview pane is max-w-[35%] + p-8, which
   * crowds the template grid — use a slightly narrower pane and padding.
   */
  const framePreviewPaneLandscapeTight =
    isLandscapeViewport &&
    !compactFrameLayout &&
    !stackPreviewTop &&
    !frameLandscapeCompact &&
    !isPotrait &&
    shortSide <= 1200
  const [remainingTime, setRemainingTime] = useState<string>(COUNTDOWN_PLACEHOLDER)
  const welcomeSettings = getPageSettings('welcome')
  const isCategoryModeEnabled = config?.settings?.categoryModeEnabled || false
  const hasBackgroundImage = Boolean(globalConfig?.theme?.backgroundImageUrl)
  const textColor = useBackgroundTextColor()
  // Over a background IMAGE, a single sampled text color is fragile — a photo can
  // be dark where the heading sits but light on average (or vice versa). Add a
  // contrast-outline shadow (opposite of the text color) so the heading, count,
  // and category chips stay legible on ANY photo, matching the scrim Result/Select
  // get from their GlassPanel. No-op on solid themes.
  const onImageTextShadow = hasBackgroundImage
    ? textColor === '#FFFFFF'
      ? '0 1px 3px rgba(0,0,0,0.75), 0 0 10px rgba(0,0,0,0.5)'
      : '0 1px 3px rgba(255,255,255,0.85), 0 0 10px rgba(255,255,255,0.6)'
    : undefined
  const onImageChipBg = hasBackgroundImage
    ? textColor === '#FFFFFF'
      ? 'rgba(0,0,0,0.28)'
      : 'rgba(255,255,255,0.4)'
    : undefined

  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)
  /**
   * Per-frame add-on badge/charge only applies when the customer picks a frame
   * BEFORE paying. When payment is taken first the total can't include a
   * per-frame amount, so treat the add-on as 0 (mirrors the server, which also
   * returns `addOnPrice: 0` in that case — this is defense in depth per spec §5/§10).
   */
  const frameAddonEligible = !welcomeSettings?.startWithPayment
  /** Resolve the chargeable add-on for a frame, honoring the eligibility gate. */
  const resolveFrameAddon = (frame?: FrameOption | null): number =>
    frameAddonEligible ? frame?.addOnPrice ?? 0 : 0

  /**
   * Resolve the per-category base price for a frame's category, honoring the same
   * eligibility gate. Prefer the currently-selected category; else the first
   * category (by order) that contains the frame. A frame in no category yields
   * price 0 / undefined name. Server already gates category mode, so `basePrice`
   * is 0 unless the whole feature is on.
   */
  const resolveCategoryBase = (frameId: string): { price: number; name?: string } => {
    if (!frameAddonEligible) return { price: 0 }
    const cats = frameCategories || []
    const contains = (c: FrameCategory): boolean =>
      (c.ids?.includes(frameId) ?? false) || (c.frames?.some((f) => f._id === frameId) ?? false)
    let cat =
      selectedCategory !== 'all' ? cats.find((c) => c.name === selectedCategory) : undefined
    if (!cat || !contains(cat)) cat = cats.find(contains)
    const price =
      cat && typeof cat.basePrice === 'number' && cat.basePrice > 0 ? cat.basePrice : 0
    return { price, name: cat?.name }
  }

  const nextPage = welcomeSettings?.startWithPayment ? 'capture' : 'checkout'

  const [currentFrames, setCurrentFrames] = useState<FrameOption[]>([])
  const [selectedFrame, setSelectedFrame] = useState<string | null>(null)

  /**
   * Natural width / height of each thumbnail, by URL, recorded as it loads (the
   * rail loads them all up front). The thumbnail is not always the canvas: Studio
   * can set it to one strip of a sheet or to an uploaded picture
   * (`thumbnailSource`), so a 3:4 sheet may show as a 1:4 strip. The preview is
   * sized from the picture; the canvas ratio is only the guess until it loads.
   */
  const [thumbnailRatios, setThumbnailRatios] = useState<Record<string, number>>({})
  const recordThumbnailRatio = useCallback(
    (thumbnail: string, event: React.SyntheticEvent<HTMLImageElement>): void => {
      const { naturalWidth, naturalHeight } = event.currentTarget
      if (!thumbnail || !naturalWidth || !naturalHeight) return
      const ratio = naturalWidth / naturalHeight
      setThumbnailRatios((prev) =>
        prev[thumbnail] === ratio ? prev : { ...prev, [thumbnail]: ratio }
      )
    },
    []
  )

  // Calculate the preview's aspect ratio for layout adjustments
  const selectedFrameData = allFrames.find((f) => f._id === selectedFrame)
  const frameAspectRatio = selectedFrameData
    ? (thumbnailRatios[selectedFrameData.thumbnail] ??
      selectedFrameData.drawParams.canvasWidth / selectedFrameData.drawParams.canvasHeight)
    : 1
  const isTallFrame = frameAspectRatio < 0.5 // For frames like 2R (500x1500 = 0.33)

  // Initialize countdown with the useCountdown hook
  const frameTimeout = config?.settings?.frameTimeout || 0
  const { setOnTick, setOnComplete, start, stop, getFormattedRemainingSeconds } =
    useCountdown(frameTimeout)

  useEffect(() => {
    let initialFramesToDisplay: FrameOption[] = []

    if (isCategoryModeEnabled && frameCategories.length > 0) {
      // Set initial selected category to the first one
      setSelectedCategory(frameCategories[0].name)
      initialFramesToDisplay = frameCategories[0].frames || []
    } else {
      // Fallback to 'all' if not in category mode or no categories
      setSelectedCategory('all')
      initialFramesToDisplay = allFrames
    }
    setCurrentFrames(initialFramesToDisplay)

    if (initialFramesToDisplay.length > 0) {
      setSelectedFrame(initialFramesToDisplay[0]._id)
    }
  }, [isCategoryModeEnabled, frameCategories, allFrames])

  useEffect(() => {
    if (isCategoryModeEnabled && selectedCategory !== 'all') {
      const category = frameCategories.find((cat) => cat.name === selectedCategory)
      setCurrentFrames(category?.frames || [])
    } else if (!isCategoryModeEnabled) {
      // In non-category mode or 'all' category selected, use all frames
      setCurrentFrames(allFrames)
    }
  }, [isCategoryModeEnabled, selectedCategory, frameCategories, allFrames])

  // When switching to a category with no frames, keep the last selected frame for preview (avoid empty / invalid src).
  // When the grid has frames, ensure selection is valid for that list.
  useEffect(() => {
    if (currentFrames.length === 0) {
      return
    }
    if (!selectedFrame || !currentFrames.some((f) => f._id === selectedFrame)) {
      setSelectedFrame(currentFrames[0]._id)
    }
  }, [currentFrames, selectedFrame])

  /** Same priority as initial load, so Next works before React effects flush */
  const handleNext = useCallback((): void => {
    const frameId =
      selectedFrame ??
      currentFrames[0]?._id ??
      (isCategoryModeEnabled ? frameCategories[0]?.frames?.[0]?._id : undefined) ??
      allFrames[0]?._id ??
      null
    if (!frameId) return

    const frame = allFrames.find((f) => f._id === frameId)
    if (!frame) return

    if (frameId !== selectedFrame) {
      setSelectedFrame(frameId)
    }

    const photoImage = frame.drawParams.images.find((img) => img.type.startsWith('photo-'))
    const photoRatio = photoImage ? photoImage.width / photoImage.height : null

    const categoryBase = resolveCategoryBase(frameId)
    setSessionState({
      userPreferences: {
        frameId,
        framePhotoRatio: photoRatio,
        frameAddonPricePerPackage: resolveFrameAddon(frame),
        frameCategoryBasePricePerOrder: categoryBase.price,
        frameCategoryNameForBase: categoryBase.name
      }
    })
    const frameName = frame.name
    logActivity(`Selected frame: ${frameName || frameId}`, { frameId })

    const next = welcomeSettings?.startWithPayment ? 'capture' : 'checkout'
    navigate(`/${projectId}/${next}`)
  }, [
    selectedFrame,
    currentFrames,
    isCategoryModeEnabled,
    frameCategories,
    selectedCategory,
    allFrames,
    setSessionState,
    logActivity,
    navigate,
    projectId,
    welcomeSettings
  ])

  useEffect(() => {
    if (frameTimeout <= 0) return
    setOnTick(() => {
      setRemainingTime(getFormattedRemainingSeconds())
    })
    setOnComplete(() => {
      logActivity('Frame selection timed out; continuing with default frame', {
        timeoutDuration: frameTimeout
      })
      handleNext()
    })
  }, [
    frameTimeout,
    setOnTick,
    setOnComplete,
    getFormattedRemainingSeconds,
    handleNext,
    logActivity
  ])

  useEffect(() => {
    if (frameTimeout <= 0) return
    start()
    return (): void => {
      stop()
    }
  }, [frameTimeout, start, stop])

  // Ballooning fix (audit: a single-column rail with height-free object-contain
  // thumbnails let 2R-strip / panoramic frames grow to 55-62% of the screen).
  // Portrait ALWAYS keeps one column — regardless of frame count. The audit once
  // split it (1 col under 6 frames, 2 cols at 6+) on the theory that a fuller
  // grid scans better; field feedback says the opposite: at 6+ frames the 2-col
  // cells got too small to judge a frame from, and guests want each frame BIG
  // even if that means scrolling. One column stays; the thumbnail's HEIGHT is
  // capped (see the thumbnail branch below) so a 2R-strip / panoramic still
  // can't balloon. Multi-column layouts (landscape) keep fixed 3:4 cells.
  const singleColPortrait = isPotrait && !stackPreviewTop
  const gridColsClass = stackPreviewTop
    ? 'grid-cols-3 gap-3 items-start'
    : frameGridLargeLandscape
      ? 'grid-cols-4 gap-6 items-start'
      : frameMobileLandscape
        ? 'grid-cols-3 gap-3 items-start'
        : frameLandscapeCompact
          ? 'grid-cols-3 gap-4 items-start'
          : singleColPortrait
            ? 'grid-cols-1 gap-4 items-center'
            : 'grid-cols-3 gap-6 items-start'

  /** Keep category chips smaller than the primary Next control; tighter strip = more tabs visible */
  const categoryStripClass = frameLandscapeCompact
    ? 'gap-1.5 px-2 py-1.5'
    : compactFrameLayout
      ? 'gap-1.5 px-2 py-2'
      : 'gap-4 px-12 py-4'
  const categoryChipClass = frameLandscapeCompact
    ? 'px-2.5 py-1 text-[11px] font-medium'
    : compactFrameLayout
      ? 'px-3 py-1.5 text-xs font-medium'
      : 'px-5 py-2 text-base font-medium'

  const categoryScrollStrip =
    isCategoryModeEnabled && frameCategories && frameCategories.length > 0 ? (
      <div
        className={`custom-scrollbar flex w-full min-w-0 flex-nowrap justify-start overflow-x-auto overflow-y-hidden ${categoryStripClass}`}
      >
        {frameCategories.map((category) => {
          // Per-category base price, shown on the chip so the customer sees the
          // price of a category before picking a frame. Gated like the add-on
          // (only when frame is picked before paying); the server already
          // returns 0 unless the whole feature is enabled.
          const categoryBase = frameAddonEligible ? category.basePrice ?? 0 : 0
          return (
          <button
            type="button"
            key={category.name}
            onClick={() => setSelectedCategory(category.name)}
            className={`shrink-0 rounded-full whitespace-nowrap transition-all ${categoryChipClass} ${
              selectedCategory === category.name ? '' : 'border'
            }`}
            style={
              selectedCategory === category.name
                ? { backgroundColor: primaryColor, color: secondaryColor }
                : {
                    borderColor: textColor,
                    color: textColor,
                    backgroundColor: onImageChipBg,
                    textShadow: onImageTextShadow
                  }
            }
          >
            {category.name}
            {categoryBase > 0 && (
              <span className="ml-1.5 font-semibold opacity-90">
                {t('frameCategoryBasePrice.badge', '+{{price}}', {
                  price: formatAmount(categoryBase, projectCurrency)
                })}
              </span>
            )}
          </button>
          )
        })}
      </div>
    ) : null

  const selectionColumn = (
    <div
      className={`relative flex min-h-0 min-w-0 flex-col ${stackPreviewTop ? 'max-w-full flex-1' : 'max-w-[70%] flex-1'}`}
      style={{ willChange: 'transform' }}
    >
      {categoryScrollStrip &&
        (hasBackgroundImage ? (
          <div className="w-full min-w-0 shrink-0 shadow-xl">{categoryScrollStrip}</div>
        ) : (
          <div className="w-full min-w-0 shrink-0 shadow-sm">{categoryScrollStrip}</div>
        ))}

      {/* Title + count: the rail scrolls, but with no count a 12-frame catalog
          looked identical to a 3-frame one (audit) — guests believed only the
          visible frames existed. */}
      {!frameLandscapeCompact && !frameMobileLandscape && (
        <div
          className={`flex shrink-0 items-baseline justify-between ${
            compactFrameLayout ? 'px-3 pt-2' : 'px-12 pt-6'
          }`}
        >
          <h1
            className={`font-bold ${compactFrameLayout ? 'text-lg' : 'text-3xl'}`}
            style={{ color: textColor, textShadow: onImageTextShadow }}
          >
            {t('frame.title', 'Choose your frame')}
          </h1>
          {currentFrames.length > 0 && (
            <span
              className={`tabular-nums ${compactFrameLayout ? 'text-xs' : 'text-base'}`}
              style={{ color: textColor, opacity: 0.7, textShadow: onImageTextShadow }}
            >
              {t('frame.count', '{{count}} frames', { count: currentFrames.length })}
            </span>
          )}
        </div>
      )}

      <div
        className={`custom-scrollbar min-h-0 flex-1 overflow-y-auto ${
          stackPreviewTop
            ? 'mt-1.5 px-2 pb-14 pt-2'
            : frameLandscapeCompact
              ? 'mt-1.5 px-1.5 py-1.5'
              : compactFrameLayout
                ? 'mt-2 px-3 py-3'
                : 'mt-4 px-12 py-6'
        }`}
      >
        <div className={`grid ${gridColsClass}`} role="listbox" title={t('frame.framesListbox', 'Frames')}>
          {currentFrames.map((frame) => (
            <button
              role="option"
              aria-label={frame.name}
              type="button"
              key={frame._id}
              onClick={() => setSelectedFrame(frame._id)}
              className={`relative flex min-w-0 cursor-pointer flex-col items-stretch text-center transition-all ${
                frameMobileLandscape
                  ? 'p-1'
                  : frameLandscapeCompact
                    ? 'p-1.5'
                    : compactFrameLayout
                      ? 'p-2'
                      : 'p-6'
              } ${selectedFrame === frame._id ? 'scale-105' : ''}`}
            >
              {/* Single-column portrait: cap the thumbnail HEIGHT (not a fixed
                  aspect box) and center it, so a 4R keeps its natural shape while
                  a 2R-strip / panoramic can't balloon past the cap. Multi-column
                  layouts use a fixed 3:4 cell so a grid of mixed aspects stays
                  uniform. Either way, no frame grows to eat the screen (audit). */}
              {singleColPortrait ? (
                // Cap goes on the IMAGE (natural aspect, centered): a 4R keeps its
                // shape, a 2R / panoramic can't grow past the height cap, and there
                // is no wrapper to collapse to 0-width before the thumbnail loads.
                <img
                  crossOrigin="anonymous"
                  src={frame.thumbnail}
                  alt={frame.name}
                  onLoad={(e) => recordThumbnailRatio(frame.thumbnail, e)}
                  className={`mx-auto max-h-[min(34vh,340px)] w-auto max-w-full rounded-lg object-contain shadow-md ${
                    selectedFrame === frame._id ? 'border-4' : 'border border-gray-100'
                  }`}
                  style={selectedFrame === frame._id ? { borderColor: primaryColor } : undefined}
                />
              ) : (
                <div
                  className={`flex aspect-[3/4] w-full items-center justify-center overflow-hidden rounded-lg ${
                    selectedFrame === frame._id ? 'border-4' : 'border border-gray-100'
                  }`}
                  style={selectedFrame === frame._id ? { borderColor: primaryColor } : undefined}
                >
                  <img
                    crossOrigin="anonymous"
                    src={frame.thumbnail}
                    alt={frame.name}
                    onLoad={(e) => recordThumbnailRatio(frame.thumbnail, e)}
                    className="max-h-full max-w-full object-contain drop-shadow-md"
                  />
                </div>
              )}
              {resolveFrameAddon(frame) > 0 && (
                <span
                  className={`pointer-events-none absolute z-[2] rounded-full font-semibold text-white shadow-md ${
                    compactFrameLayout
                      ? 'right-1 top-1 px-1.5 py-0.5 text-[10px]'
                      : 'right-2 top-2 px-2.5 py-1 text-sm'
                  }`}
                  style={{ backgroundColor: primaryColor }}
                >
                  {t('frameSurcharge.badge', '+{{price}}', {
                    price: formatAmount(resolveFrameAddon(frame), projectCurrency)
                  })}
                </span>
              )}
              {!stackPreviewTop && showFrameTitle && (
                <p
                  className={`line-clamp-2 min-h-0 w-full max-w-full break-words ${
                    frameMobileLandscape || (isLandscapeViewport && compactFrameLayout)
                      ? 'mt-1 text-[10px] leading-tight'
                      : compactFrameLayout
                        ? 'mt-1 text-xs leading-snug'
                        : 'mt-2 text-xl leading-snug'
                  }`}
                  style={{ color: textColor }}
                  title={frame.name}
                >
                  {frame.name}
                </p>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  const previewPanel = (
    <GlassPanel
      className={`flex min-h-0 min-w-0 w-full flex-col items-center ${
        stackPreviewTop
          ? 'max-h-[45%] max-w-full shrink-0 overflow-hidden p-2'
          : frameLandscapeCompact
            ? 'h-full max-w-[40%] flex-1 overflow-hidden p-2'
            : isPotrait
              ? `h-full max-w-[55%] overflow-hidden ${compactFrameLayout ? 'p-8' : 'p-12'}`
              : framePreviewPaneLandscapeTight
                ? 'h-full max-w-[30%] min-w-0 overflow-hidden p-6'
                : 'h-full max-w-[35%] overflow-hidden p-8'
      }`}
    >
      <div
        className={`flex w-full min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden ${
          framePreviewAspectBox
            ? framePreviewPaneLandscapeTight
              ? 'px-0.5 py-0.5'
              : 'px-1 py-1'
            : 'px-2'
        } ${isTallFrame && !framePreviewAspectBox ? 'custom-scrollbar overflow-y-auto' : ''}`}
      >
        {selectedFrameData && framePreviewAspectBox ? (
          <img
            src={selectedFrameData.thumbnail || ''}
            alt={t('frame.previewAlt', 'Preview')}
            crossOrigin="anonymous"
            onLoad={(e) => recordThumbnailRatio(selectedFrameData.thumbnail, e)}
            className={`max-h-full max-w-full rounded-lg object-contain object-center drop-shadow-xl ${
              framePreviewPaneLandscapeTight ? 'max-h-[min(100%,92%)]' : ''
            }`}
          />
        ) : (
          // Fixed width, but never taller than the pane: a strip thumbnail of a
          // sheet frame (1:4) would otherwise run off the bottom and be clipped.
          <div
            className={`flex h-full min-h-0 items-center justify-center px-4 ${
              compactFrameLayout
                ? 'w-full max-w-[320px]'
                : `${isTallFrame ? 'w-[300px]' : 'w-[426px]'} shrink-0`
            }`}
          >
            <img
              src={selectedFrameData?.thumbnail || ''}
              alt={t('frame.previewAlt', 'Preview')}
              crossOrigin="anonymous"
              onLoad={(e) =>
                selectedFrameData && recordThumbnailRatio(selectedFrameData.thumbnail, e)
              }
              className="max-h-full w-full object-contain drop-shadow-xl"
            />
          </div>
        )}
      </div>

      <div
        className={`flex shrink-0 flex-col gap-2 pt-2 ${
          stackPreviewTop ? 'items-center' : 'w-full min-w-0 items-center justify-center px-2 sm:px-3'
        }`}
      >
        {/* Selected-frame name in the preview panel — previously only the
            stacked layout showed it; the row layout left selection state living
            only in the rail's border (audit). */}
        {showFrameTitle && selectedFrameData && (
          <p
            className={`line-clamp-2 max-w-full px-2 text-center font-medium leading-tight text-carbon/90 ${
              stackPreviewTop || compactFrameLayout ? 'text-xs' : 'text-lg'
            }`}
            title={selectedFrameData.name}
          >
            {selectedFrameData.name}
          </p>
        )}
        {(config?.settings?.frameTimeout || 0) > 0 && (
          <span
            className={`self-center font-mono tabular-nums ${
              isCountdownUrgent(remainingTime) ? '!font-bold !text-red-600' : 'text-carbon'
            } ${compactFrameLayout ? 'text-lg' : 'text-2xl'}`}
          >
            {remainingTime}
          </span>
        )}
        {!stackPreviewTop && (
          <Button
            text={t('common.next', 'Next')}
            onClick={handleNext}
            data-nav-to={nextPage}
            className={frameNextRowBtn}
          />
        )}
      </div>
    </GlassPanel>
  )

  return (
    <BackgroundContainer
      className={`relative flex h-full min-h-0 ${stackPreviewTop ? 'flex-col' : 'flex-row'}`}
      data-page="frame"
    >
      {stackPreviewTop ? (
        <>
          {previewPanel}
          {selectionColumn}
        </>
      ) : (
        <>
          {selectionColumn}
          {previewPanel}
        </>
      )}
      {stackPreviewTop && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[100] flex justify-end p-0">
                <div className="pointer-events-auto pr-4 pb-12">
            <Button
              text={t('common.next', 'Next')}
              onClick={handleNext}
              data-nav-to={nextPage}
              className={`${frameNextStackedBtn} shadow-lg`}
            />
          </div>
        </div>
      )}
    </BackgroundContainer>
  )
}

export default FramePage
